/* eslint-disable react-hooks/exhaustive-deps */
import {
  ArrowDownOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LineChartOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Empty,
  message,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Select,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import introJs from 'intro.js';
import { Steps as StepsIntro } from 'intro.js-react';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import IconCameraEmpty from '../../assets/icons/camera-empty.svg';
import IconDecryptionButton from '../../assets/icons/decryption-button.svg';
import IconExclamation from '../../assets/icons/exclamation.svg';
import IconPlayButton from '../../assets/icons/play-button.svg';
import IntroButtonAdd from '../../assets/images/intro-button-add.png';
import IntroTabRtsp from '../../assets/images/intro-tab-rtsp.png';
import Thumbnail from '../../assets/images/thumbnail.jpg';
import PlayerComponent from '../../components/Player';
import Spinner from '../../components/Spinner';
import * as SERVICE from '../../constants/service.types';
import {
  generateLinkStorageFileReturn,
  removeStorageFileReturn,
  resetCameraStorageReturn,
  setListStorageFileByTag,
  setOpenUploadFilesModal,
  setPlayVideoAtPage,
  setUploadFilesCamera,
  setUploadFilesDate,
  setUploadFilesOldVideos,
  socketEmitEvent,
} from '../../redux/actions';
import { downloadFile } from '../../untils/download';
import SearchVideosModal from './SearchVideosModal/SearchVideosModal';
import VideoInformation from './VideoInformation';
import SystemMessage from './SystemMessage';
import { beeXLogger } from '../../helpers/logger';
import PlaybackPlayer from '../../components/Playback';
import {
  updateRecoveryCameraPage,
} from '../../redux/recovery/action';
import { getRecoveryCameraPage } from '../../redux/recovery/reducer';
import {
  getPlaybackTimesLocal,
  downloadPlaybackOnDevice,
} from '../../socket/playback';
import {
  secondsToTime,
  timeToSeconds,
  genDownloadKeyLocal,
} from '../../components/Playback/utils';
import { downloaderInstance } from '../../components/Playback/downloader';
import Downloader from '../../components/Playback/downloader';

const { Option } = Select;
const { confirm } = Modal;

const FULL_RECORD_TIMES = Array.from({ length: 24 }, (_, i) => ({
  value: i + 100,
  title: `${i < 10 ? '0' + i : i}:00 - ${i === 23 ? '00' : i < 9 ? '0' + (i + 1) : i + 1
    }:00`,
}));

function normalize(segments, mergeGapSeconds = 1) {
  if (!segments.length) return [];

  const sorted = [...segments].sort((a, b) => a.from - b.from);
  const result = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = result[result.length - 1];
    const curr = sorted[i];

    if (curr.from <= prev.to + mergeGapSeconds) {
      prev.to = Math.max(prev.to, curr.to);
    } else {
      result.push(curr);
    }
  }

  return result;
}

function clipToDayRange(times = [], dayStart = 0, dayEnd = 86400) {
  const result = [];

  for (const item of times) {
    let { from, to } = item;

    if (to < from) {
      to = dayEnd;
    }

    from = Math.max(from, dayStart);
    to = Math.min(to, dayEnd);

    if (to > from) {
      result.push({ ...item, from, to });
    }
  }

  return result;
}

function getMissingTimeSegments(cloudTimes = [], localTimes = []) {
  beeXLogger.debug("🚀 getMissingTimeSegments - localTimes:", localTimes);
  beeXLogger.debug("🚀 getMissingTimeSegments - cloudTimes:", cloudTimes);

  const clippedCloudTimes = clipToDayRange(cloudTimes);
  const cloud = normalize(clippedCloudTimes, 60);

  const groups = localTimes.reduce((acc, item) => {
    const key = item.type || "gateway";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const result = [];

  for (const type in groups) {
    const local = normalize(groups[type]);

    let i = 0;

    for (const segment of local) {
      const { from: lStart, to: lEnd } = segment;

      while (i < cloud.length && cloud[i].to <= lStart) {
        i++;
      }

      let current = lStart;
      let k = i;

      while (k < cloud.length && cloud[k].from < lEnd) {
        const c = cloud[k];

        if (c.from > current) {
          result.push({
            from: current,
            to: Math.min(c.from, lEnd),
            type,
          });
        }

        current = Math.max(current, c.to);

        if (current >= lEnd) break;

        k++;
      }

      if (current < lEnd) {
        result.push({
          from: current,
          to: lEnd,
          type,
        });
      }
    }
  }

  result.sort((a, b) => {
    if (a.from !== b.from) return a.from - b.from;
    return a.type.localeCompare(b.type);
  });

  beeXLogger.debug("🚀 getMissingTimeSegments - result:", result);

  return result;
}

const MAX_DURATION = 10 * 60;

function splitSegments(segments) {
  const result = [];

  segments.forEach((seg) => {
    let start = seg.from;

    beeXLogger.debug('🚀 ~ splitSegments ~ segments:', segments);
    while (start < seg.to) {
      const end = Math.min(start + MAX_DURATION, seg.to);

      result.push({
        from: start,
        to: end,
        type: seg.type
      });

      start = end;
    }
  });

  beeXLogger.debug('🚀 ~ splitSegments ~ result:', result);
  return result;
}

const StorageVideoComponent = ({
  serial,
  handleAddCamera,
  permission,
  selectCamera,
}) => {
  const { t } = useTranslation();
  const {
    cameraStorage,
    decryptData,
    playVideoAtPage,
    namespace,
    camerasDate,
    dataUser,
    lastVisited,
    uploadStorageFile,
    servicesCamera,
    removeStorageFile,
    uploadFilesCamera,
  } = useSelector((state) => state);
  const popoverRef = useRef(null);

  const [ipAddress, setIpAddress] = useState('0.0.0.0');
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setDownloading] = useState(false);
  const [dateRecord, setDateRecord] = useState(moment());
  const [timeRecord, setTimeRecord] = useState(FULL_RECORD_TIMES);
  const [data, setData] = useState();
  const [timePicker, setTimePicker] = useState(1000);
  const [videos, setVideos] = useState();
  const [gatewayVideos, setGatewayVideos] = useState([]);
  const [dataSelectSource, setDataSelectSource] = useState();
  const [currentVideo, setCurrentVideo] = useState(null)
  const [dataSteps, setDataSteps] = useState([
    {
      element: '.intro-add-camera',
      intro: (
        <div>
          <img
            alt=""
            style={{ width: '100%', marginBottom: '8px' }}
            src={IntroButtonAdd}
          />
          <div>{t('Nhấn vào nút "Add camera"')}</div>
          <img
            alt=""
            style={{ width: '100%', margin: '16px 0px 8px' }}
            src={IntroTabRtsp}
          />
          <div>{t('Sau đó chọn "RTSP link"')}</div>
        </div>
      ),
    },
  ]);
  const [stepsEnabled, setStepsEnabled] = useState(false);
  const [initialStep, setInitialStep] = useState(0);

  const [isUploadVideo, setIsUploadVideo] = useState(false);
  const [dataRequestId, setDataRequestId] = useState(new Date().getTime());

  const [isOpenSearchVideosModal, setOpenSearchVideosModal] = useState(false);
  const [downloadVideos, setDownloadVideos] = useState();
  const [visible, setVisible] = useState(false);
  const [errorVideo, setErrorVideo] = useState([]);
  const dispatch = useDispatch();
  const {
    selectedDate: recoverySelectedDate,
    selectedTimes: recoverySelectedTimes,
    analyticsVisibile: recoveryAnalyticsVisible,
    searchModalVisible: recoverySearchModalVisible,
  } = useSelector(getRecoveryCameraPage);
  const [playbackLocalTimes, setPlaybackLocalTimes] = useState([]);
  const bulkGatewayKeysRef = useRef(new Set());
  const normalDownloadPendingRef = useRef(0);
  const cameraStorageRequestPendingRef = useRef(false);
  const cameraStorageTimeoutRef = useRef(null);
  const [failedDownloads, setFailedDownloads] = useState([]);
  const [isFailedModalVisible, setFailedModalVisible] = useState(false);
  const [videoDebugLogsMap, setVideoDebugLogsMap] = useState({});

  const updateVideoDebugLog = useCallback((recordId, stepKey, stepData) => {
    if (!recordId) return;
    setVideoDebugLogsMap((prev) => {
      const currentRecordLog = prev[recordId] || {};
      const currentStepData = currentRecordLog[stepKey] || {};

      const isUnchanged = Object.keys(stepData).every(
        (k) => currentStepData[k] === stepData[k],
      );
      if (isUnchanged) return prev;

      return {
        ...prev,
        [recordId]: {
          ...currentRecordLog,
          [stepKey]: {
            ...currentStepData,
            ...stepData,
          },
        },
      };
    });
  }, []);

  useEffect(() => {
    if (dataUser) {
      if (!lastVisited && localStorage.getItem('tutorial') === '1') {
        setTimeout(() => {
          setStepsEnabled(true);
        }, 300);
      }
    }
  }, [dataUser]);

  useEffect(() => {
    setTimeout(() => {
      introJs().start();
    }, 300);
    return () => {
      dispatch(resetCameraStorageReturn());
      introJs().exit();
    };
  }, []);

  useEffect(() => {
    function sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }
    async function onDecrypt(callback) {
      await sleep(2000);
      return callback();
    }

    if (decryptData && playVideoAtPage === 'PLAY_VIDEO_IN_STORAGE_VIDEO') {
      beeXLogger.debug(
        '🚀 ~ StorageVideoComponent ~ decryptData:',
        decryptData,
      );
      if (decryptData.secureLink) {
        updateVideoDebugLog(decryptData.recordId, 's3Link', {
          status: 'success',
          text: 'S3 link received successfully via WebSocket',
          url: decryptData.secureLink,
        });
        updateVideoDebugLog(decryptData.recordId, 's3Playback', {
          status: 'pending',
          text: 'Loading S3 video into player...',
        });
      } else {
        updateVideoDebugLog(decryptData.recordId, 's3Link', {
          status: 'error',
          text: 'Failed to receive S3 link',
          error: 'WebSocket returned error or empty secureLink',
        });
        updateVideoDebugLog(decryptData.recordId, 'fallback', {
          status: 'triggered',
          text: 'S3 link failed, fallback to gateway/DVR stream',
        });
        setErrorVideo((prev) => [...new Set([...prev, decryptData.recordId])]);
        setVideos((state) => {
          const target = state?.find(
            (v) =>
              v?.recordId === decryptData.recordId ||
              v?.dataRecordIds?.includes(decryptData.recordId),
          );
          if (target) {
            setCurrentVideo(target);
          }
          return state;
        });
      }
      onDecrypt(() => {
        setVideos((state) =>
          state?.map((video) =>
            video?.recordId !== decryptData.recordId &&
              !video?.dataRecordIds?.find((item) => item === decryptData.recordId)
              ? video
              : {
                ...video,
                decrypting: false,
                secureLink: decryptData.secureLink,
              },
          ),
        );
      });
    } else if (
      decryptData &&
      playVideoAtPage === 'DOWNLOAD_VIDEO_IN_STORAGE_VIDEO'
    ) {
      if (decryptData.secureLink && downloadVideos?.length > 0) {
        setDownloading(true);
        downloadFile({
          url: decryptData.secureLink,
          fileName: decryptData.fileName,
          isSaveAs: decryptData.fileName ? true : false,
          onFetched: () => {
            normalDownloadPendingRef.current = Math.max(
              0,
              normalDownloadPendingRef.current - 1,
            );
            checkAndFlushGatewayBlobs();
          },
        });
        setTimeout(() => {
          setDownloadVideos(
            downloadVideos?.filter(
              (item) => item?.recordId !== decryptData.recordId,
            ),
          );
          setDownloading(false);
        }, 5000);
      }
    }
  }, [decryptData, ipAddress, playVideoAtPage]);

  useEffect(() => {
    if (downloadVideos?.length > 0 && ipAddress) {
      let dataBody = {
        ipAddress,
        recordId: downloadVideos?.[0]?.recordId,
        namespace,
      };
      let dataObj = {
        appServiceType: SERVICE.GENERATE_LINK_STORAGE_FILE,
        requestId: new Date().getTime(),
        body: JSON.stringify(dataBody),
      };
      dispatch(setPlayVideoAtPage('DOWNLOAD_VIDEO_IN_STORAGE_VIDEO'));
      dispatch(socketEmitEvent(dataObj));
    }
  }, [downloadVideos]);

  useEffect(() => {
    const unsubscribe = downloaderInstance.subscribe(({ key, state }) => {
      if (!bulkGatewayKeysRef.current.has(key)) return;

      if (state.status === 'completed') {
        checkAndFlushGatewayBlobs();
      } else if (state.status === 'error') {
        const segMeta = bulkGatewayMetaRef.current.get(key);
        if (segMeta) {
          setFailedDownloads((prev) => {
            const already = prev.some((f) => f.key === key);
            if (already) return prev;
            return [...prev, { key, ...segMeta }];
          });
        }
      }
    });
    return unsubscribe;
  }, []);

  const bulkGatewayMetaRef = useRef(new Map());

  const checkAndFlushGatewayBlobs = useCallback(() => {
    if (normalDownloadPendingRef.current > 0) return;
    if (bulkGatewayKeysRef.current.size === 0) return;

    bulkGatewayKeysRef.current.forEach((key) => {
      const state = downloaderInstance.getState(key);
      if (state?.status === 'completed') {
        const blob = downloaderInstance.getBlob(key);
        const meta = bulkGatewayMetaRef.current.get(key);
        if (blob && meta) {
          Downloader.downloadBlob(blob, meta.filename);
        }
        bulkGatewayKeysRef.current.delete(key);
        bulkGatewayMetaRef.current.delete(key);
      }
    });
  }, []);

  const handleDownloadVideo = (isDownload, dataVideos) => {
    confirm({
      title: t('Are you sure to download videos?'),
      icon: <ExclamationCircleOutlined />,

      onOk() {
        startBulkDownload(dataVideos);
      },
      onCancel() { },
    });
  };

  const startBulkDownload = useCallback(
    (dataVideos) => {
      const normalVideos = dataVideos.filter((v) => v.type !== 'on_gateway');
      const gatewayVideos = dataVideos.filter((v) => v.type === 'on_gateway');

      bulkGatewayKeysRef.current = new Set();
      bulkGatewayMetaRef.current = new Map();
      normalDownloadPendingRef.current = normalVideos.length;
      setFailedDownloads([]);

      if (normalVideos.length > 0) {
        setDownloadVideos(normalVideos);
        setDownloading(true);
      } else {
        normalDownloadPendingRef.current = 0;
      }

      if (gatewayVideos.length === 0) return;

      const datePart = moment(dateRecord);

      gatewayVideos.forEach((video) => {
        const fromTimePart = moment(video.from, 'HH:mm:ss');
        const toTimePart = moment(video.to, 'HH:mm:ss');

        const fromTimeFormatted = datePart
          .clone()
          .set({
            hour: fromTimePart.hour(),
            minute: fromTimePart.minute(),
            second: fromTimePart.second(),
          })
          .format('YYYY-MM-DD HH:mm:ss');

        const toTimeFormatted = datePart
          .clone()
          .set({
            hour: toTimePart.hour(),
            minute: toTimePart.minute(),
            second: toTimePart.second(),
          })
          .format('YYYY-MM-DD HH:mm:ss');

        const downloadKeyLocal = genDownloadKeyLocal(
          video.serial,
          fromTimeFormatted,
          toTimeFormatted,
        );

        const filename = `playback-${video.serial}-${video.from.replaceAll(':', '-')}-${video.to.replaceAll(':', '-')}.mp4`;

        bulkGatewayKeysRef.current.add(downloadKeyLocal);
        bulkGatewayMetaRef.current.set(downloadKeyLocal, {
          from: video.from,
          to: video.to,
          serial: video.serial,
          filename,
        });

        downloadPlaybackOnDevice({
          namespace,
          serial: video.serial,
          fromTime: fromTimeFormatted,
          toTime: toTimeFormatted,
        })
          .then((res) => {
            if (!res || !res?.downloadUrl) {
              setFailedDownloads((prev) => {
                const already = prev.some((f) => f.key === downloadKeyLocal);
                if (already) return prev;
                return [
                  ...prev,
                  {
                    key: downloadKeyLocal,
                    from: video.from,
                    to: video.to,
                    serial: video.serial,
                  },
                ];
              });
              bulkGatewayKeysRef.current.delete(downloadKeyLocal);
              bulkGatewayMetaRef.current.delete(downloadKeyLocal);
              return;
            }

            let url = res?.downloadUrl?.includes('/playbacks')
              ? res?.downloadUrl
              : res?.downloadUrl?.replace(
                video.serial,
                `playbacks / ${video.serial} `,
              );
            url = url?.replace('playback01.beex.vn', 'playback01.beex.vn:5199');

            setTimeout(() => {
              downloaderInstance.download(
                downloadKeyLocal,
                url,
                res?.downloadType,
                res?.fileSize || 0,
              );
            }, 5000);
          })
          .catch((err) => {
            beeXLogger.error('🚀 ~ startBulkDownload ~ on_gateway error:', err);
            setFailedDownloads((prev) => {
              const already = prev.some((f) => f.key === downloadKeyLocal);
              if (already) return prev;
              return [
                ...prev,
                {
                  key: downloadKeyLocal,
                  from: video.from,
                  to: video.to,
                  serial: video.serial,
                },
              ];
            });
            bulkGatewayKeysRef.current.delete(downloadKeyLocal);
            bulkGatewayMetaRef.current.delete(downloadKeyLocal);
          });
      });
    },
    [namespace, dateRecord],
  );

  useEffect(() => {
    if (failedDownloads.length > 0) {
      setFailedModalVisible(true);
    }
  }, [failedDownloads]);

  const displayVideos = useMemo(() => {
    beeXLogger.debug('🚀 ~ StorageVideoComponent ~ videos:', videos);
    const normal = videos ?? [];
    const filtered =
      timePicker === 1000
        ? gatewayVideos
        : gatewayVideos.filter((v) => v.hour === timePicker - 100);
    beeXLogger.debug('🚀 ~ StorageVideoComponent ~ filtered:', filtered);

    const map = new Map();
    [...normal, ...filtered].forEach((v) => {
      const key = `${v.from}-${v.to}`;
      if (!map.has(key)) map.set(key, v);
    });

    beeXLogger.debug(
      '🚀 ~ StorageVideoComponent ~ displayVideos:',
      Array.from(map.values()),
    );
    return Array.from(map.values()).sort(
      (a, b) => timeToSeconds(b.from) - timeToSeconds(a.from),
    );
  }, [videos, gatewayVideos, timePicker]);

  useEffect(() => {
    if (!Array.isArray(cameraStorage)) {
      return;
    }

    if (cameraStorageTimeoutRef.current) {
      clearTimeout(cameraStorageTimeoutRef.current);
      cameraStorageTimeoutRef.current = null;
    }

    if (cameraStorage.length === 0) {
      if (!cameraStorageRequestPendingRef.current) {
        return;
      }

      cameraStorageRequestPendingRef.current = false;
      setData([]);
      // setTimeRecord([]);
      setIsLoading(false);
      return;
    }

    cameraStorageRequestPendingRef.current = false;

    if (cameraStorage.length > 0) {
      beeXLogger.debug(
        '🚀 ~ StorageVideoComponent ~ cameraStorage:',
        cameraStorage,
      );
      let times = [];

      cameraStorage.map(
        (video) =>
          !times.some((time) => time.value - 100 === video.hour) &&
          times.push({
            value: video.hour + 100,
            title: `${video.hour < 10 ? '0' + video.hour : video.hour}:00 - ${video.hour === 23
              ? '00'
              : video.hour < 9
                ? '0' + (video.hour + 1)
                : video.hour + 1
              }:00`,
          }),
      );

      times.sort((a, b) => a.value - b.value);

      setData([...cameraStorage].reverse());
      // setTimeRecord(times);
      setIsLoading(false);
    }
  }, [cameraStorage]);

  useEffect(() => {
    if (displayVideos) {
      beeXLogger.debug(
        '🚀 ~ StorageVideoComponent ~ displayVideos:',
        displayVideos,
      );
      let times = [];

      displayVideos.map(
        (video) =>
          !times.some((time) => time.value - 100 === video.hour) &&
          times.push({
            value: video.hour + 100,
            title: `${video.hour < 10 ? '0' + video.hour : video.hour}:00 - ${video.hour === 23
              ? '00'
              : video.hour < 9
                ? '0' + (video.hour + 1)
                : video.hour + 1
              }:00`,
          }),
      );

      times.sort((a, b) => a.value - b.value);

      // setTimeRecord(times);
    }
  }, [displayVideos]);

  useEffect(() => {
    if (data && timePicker) {
      let videos = [];
      data.forEach((video) => {
        if (
          !videos?.find(
            (item) => item.from === video.from && item.minute === video.minute,
          )
        ) {
          const dataTemp = data?.filter(
            (item) => item.from === video.from && item.minute === video.minute,
          );
          videos.push({
            ...video,
            loading: false,
            dataRecordIds: dataTemp?.map((item) => item.recordId),
          });
        }
      });
      videos.sort((a, b) => {
        return b?.from?.localeCompare(a?.from, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });
      if (timePicker === 1000) {
        setVideos(videos);
      } else {
        setVideos(videos.filter((video) => video.hour === timePicker - 100));
      }
    }
  }, [data, timePicker]);

  const onLogService = async (serial) => {
    const timeRequest = new Date().getTime();
    let data = {
      timeRequest,
      namespace,
      serial,
    };
    let dataObj = {
      appServiceType: SERVICE.LIST_SERVICE_BY_DEVICE_SERIAL,
      requestId: timeRequest,
      body: JSON.stringify(data),
    };
    return dispatch(socketEmitEvent(dataObj));
  };

  useEffect(() => {
    if (selectCamera) {
      onLogService(selectCamera.serial);
    }
  }, [selectCamera]);

  useEffect(() => {
    if (servicesCamera) {
      setIsUploadVideo(
        servicesCamera?.find((item) => item.id === 10 && item.licenseCode) &&
        selectCamera?.permission
          ?.split('_')
          ?.find((item) => item === 'upload'),
      );
    }
  }, [servicesCamera]);

  const onViewVideo = (recordId, encrypted) => {
    const isGateway =
      typeof recordId === 'string' && recordId.startsWith('on_gateway-');
    if (isGateway) {
      return;
    }

    updateVideoDebugLog(recordId, 's3Link', {
      status: 'pending',
      text: 'Sending GENERATE_LINK_STORAGE_FILE request via WebSocket...',
    });
    updateVideoDebugLog(recordId, 's3Playback', {
      status: 'skipped',
      text: 'S3 video not loaded yet',
    });

    setVideos((state) => {
      return state?.map((video) =>
        video?.recordId !== recordId &&
          !video?.dataRecordIds?.find((item) => item === recordId)
          ? video
          : { ...video, loading: true, decrypting: encrypted },
      );
    });

    if (recordId && ipAddress) {
      let dataBody = {
        ipAddress,
        recordId,
        namespace,
      };
      beeXLogger.debug("🚀 ~ GENERATE_LINK_STORAGE_FILE ~ dataBody:", dataBody)
      let dataObj = {
        appServiceType: SERVICE.GENERATE_LINK_STORAGE_FILE,
        requestId: new Date().getTime(),
        body: JSON.stringify(dataBody),
      };

      dispatch(setPlayVideoAtPage('PLAY_VIDEO_IN_STORAGE_VIDEO'));
      return dispatch(socketEmitEvent(dataObj));
    }
  };

  const getListStorage = useCallback(
    (serial, dateRecord) => {
      if (cameraStorageTimeoutRef.current) {
        clearTimeout(cameraStorageTimeoutRef.current);
      }

      cameraStorageRequestPendingRef.current = true;
      cameraStorageTimeoutRef.current = setTimeout(() => {
        if (!cameraStorageRequestPendingRef.current) {
          return;
        }

        cameraStorageTimeoutRef.current = null;
        cameraStorageRequestPendingRef.current = false;
        setData([]);
        setTimeRecord([]);
        setIsLoading(false);
      }, 10000);

      let data = {
        serial,
        dateRecord: dateRecord.format('YYYY-MM-DD'),
        namespace,
      };
      let dataObj = {
        appServiceType: SERVICE.LIST_CAMERA_STORAGE_FILE,
        requestId: new Date().getTime(),
        body: JSON.stringify(data),
      };

      dispatch(socketEmitEvent(dataObj));
    },
    [dispatch, namespace],
  );

  const getListStorageDate = useCallback(
    (serial) => {
      let data = {
        serial,
        namespace,
      };
      beeXLogger.debug('🚀 ~ LIST_CAMERA_STORAGE_FILE_DATE ~ data:', data);
      let dataObj = {
        appServiceType: SERVICE.LIST_CAMERA_STORAGE_FILE_DATE,
        requestId: new Date().getTime(),
        body: JSON.stringify(data),
      };

      dispatch(socketEmitEvent(dataObj));
    },
    [dispatch, namespace],
  );

  useEffect(() => {
    if (dateRecord && serial) {
      getListStorage(serial, dateRecord);
      setIsLoading(true);
      setCurrentVideo(null);
      setErrorVideo([]);
    }
  }, [dateRecord, getListStorage, serial]);

  useEffect(() => {
    return () => {
      if (cameraStorageTimeoutRef.current) {
        clearTimeout(cameraStorageTimeoutRef.current);
        cameraStorageTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (serial) {
      getListStorageDate(serial);
    }
  }, [getListStorageDate, serial]);

  useEffect(() => {
    if (
      uploadStorageFile?.isSuccess &&
      selectCamera &&
      uploadStorageFile?.data.serial === selectCamera?.serial
    ) {
      getListStorage(serial, dateRecord);
      setIsLoading(true);
    }
  }, [uploadStorageFile]);

  useEffect(() => {
    if (removeStorageFile === dataRequestId) {
      dispatch(removeStorageFileReturn(null));
      getListStorage(serial, dateRecord);
      setIsLoading(true);
    }
  }, [removeStorageFile]);

  const handleReportActionLog = (logContent, logSerial) => {
    let data = {
      action: 'Download',
      content: logContent,
      namespace,
      serial: logSerial,
    };
    let dataObj = {
      appServiceType: SERVICE.REPORT_ACTION_LOG,
      requestId: new Date().getTime(),
      body: JSON.stringify(data),
    };
    dispatch(socketEmitEvent(dataObj));
  };

  const showModalUpload = () => {
    if (!uploadFilesCamera) {
      dispatch(setUploadFilesCamera(selectCamera));
      dispatch(setUploadFilesDate(dateRecord));
      dispatch(setUploadFilesOldVideos(videos));
      dispatch(setOpenUploadFilesModal(true));
    } else {
      message.warning('There is another camera processing the video upload');
    }
  };

  const handleDeleteRecord = (dataVideo) => {
    const timeRequest = new Date().getTime();
    let data = {
      serial: dataVideo.serial,
      namespace,
      recordId: dataVideo.recordId,
    };
    let dataObj = {
      appServiceType: SERVICE.REMOVE_STORAGE_FILE,
      requestId: timeRequest,
      body: JSON.stringify(data),
    };
    setDataRequestId(timeRequest);
    dispatch(socketEmitEvent(dataObj));
  };

  const handleClosePopup = (visible) => {
    setVisible(false);
    dispatch(updateRecoveryCameraPage({ analyticsVisibile: false }));
  };

  const onChangeDate = (date) => {
    setDateRecord(date);
    dispatch(updateRecoveryCameraPage({ selectedDate: date }));
  };

  useEffect(() => {
    if (
      recoverySelectedDate &&
      !moment(recoverySelectedDate).isSame(dateRecord, 'day')
    ) {
      setDateRecord(moment(recoverySelectedDate));
    }
  }, [recoverySelectedDate, dateRecord]);

  const onChangeTime = (times) => {
    setTimePicker(times);
    dispatch(updateRecoveryCameraPage({ selectedTimes: times }));
  };

  useEffect(() => {
    if (recoverySelectedTimes && recoverySelectedTimes !== timePicker) {
      setTimePicker(recoverySelectedTimes);
    }
  }, [recoverySelectedTimes, timePicker]);

  useEffect(() => {
    if (recoveryAnalyticsVisible !== visible) {
      setVisible(recoveryAnalyticsVisible);
    }
  }, [recoveryAnalyticsVisible, visible]);

  useEffect(() => {
    if (recoverySearchModalVisible !== isOpenSearchVideosModal) {
      setOpenSearchVideosModal(recoverySearchModalVisible);
    }
  }, [recoverySearchModalVisible, isOpenSearchVideosModal]);

  useEffect(() => {
    if (!serial || !namespace || !dateRecord) {
      setPlaybackLocalTimes([]);
      setGatewayVideos([]);
      return;
    }
    setPlaybackLocalTimes([]);
    setGatewayVideos([]);

    const input = {
      serial,
      namespace,
      fromTime: moment(dateRecord).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      toTime: moment(dateRecord).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    };
    getPlaybackTimesLocal(input)
      .then((res) => {
        if (
          !res ||
          res?.serial !== serial ||
          res?.fromTime !== input.fromTime ||
          res?.toTime !== input.toTime
        ) {
          setPlaybackLocalTimes([]);
          setGatewayVideos([]);
          return;
        }
        const storageTimes = res?.storageTimes || [];
        beeXLogger.debug("🚀 ~ StorageVideoComponent ~ getPlaybackTimesLocal:", res)
        beeXLogger.debug("🚀 ~ StorageVideoComponent ~ storageTimes:", storageTimes)
        setPlaybackLocalTimes(storageTimes);
        setGatewayVideos([]);
      })
      .catch((err) => {
        beeXLogger.error(
          '🚀 ~ StorageVideoComponent ~ getPlaybackTimesLocal - err:',
          err,
        );
        setPlaybackLocalTimes([]);
        setGatewayVideos([]);
      });
  }, [serial, namespace, dateRecord]);

  useEffect(() => {
    if (!serial || !namespace || !dateRecord) {
      setGatewayVideos([]);
      return;
    }
    if (!playbackLocalTimes?.length) {
      setGatewayVideos([]);
      return;
    }

    const recordDateStr = dateRecord.format('YYYY-MM-DD');
    const startOfDay = moment(recordDateStr + ' 00:00:00');
    const endOfDay = moment(recordDateStr + ' 23:59:59');

    const isToday = moment(dateRecord).isSame(moment(), 'day');
    const nowSeconds = isToday
      ? timeToSeconds(moment().format('HH:mm:ss'))
      : null;

    let templeData = [...(data || [])]
      .map((item) => ({
        from: timeToSeconds(item.from),
        to: timeToSeconds(item.to),
      }))
      .sort((a, b) => a.from - b.from);

    let templeLocalTimes = [...playbackLocalTimes]
      .map((item) => {
        if (!item || !item.from || !item.to) return null;

        const fromStr = String(item.from).includes(' ')
          ? item.from
          : `${recordDateStr} ${item.from}`;
        const toStr = String(item.to).includes(' ')
          ? item.to
          : `${recordDateStr} ${item.to}`;

        const fromMoment = moment(fromStr);
        const toMoment = moment(toStr);

        if (!fromMoment.isValid() || !toMoment.isValid()) return null;
        if (
          toMoment.isSameOrBefore(startOfDay) ||
          fromMoment.isSameOrAfter(endOfDay) ||
          toMoment.isSameOrBefore(fromMoment)
        ) {
          return null;
        }

        const clampedFrom = fromMoment.isBefore(startOfDay)
          ? startOfDay
          : fromMoment;
        const clampedTo = toMoment.isAfter(endOfDay) ? endOfDay : toMoment;

        const fromSec = timeToSeconds(clampedFrom.format('HH:mm:ss'));
        let toSec = timeToSeconds(clampedTo.format('HH:mm:ss'));

        if (nowSeconds !== null) {
          toSec = Math.min(toSec, nowSeconds);
        }

        if (fromSec >= toSec) return null;

        return {
          from: fromSec,
          to: toSec,
          type: item.type || '',
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.from - b.from);

    beeXLogger.debug(
      '🚀 ~ StorageVideoComponent ~ templeLocalTimes - after filtering:',
      templeLocalTimes,
    );

    if (!templeLocalTimes.length) {
      setGatewayVideos([]);
      return;
    }

    let missingSegments = getMissingTimeSegments(templeData, templeLocalTimes);
    beeXLogger.debug(
      '🚀 ~  ~ missingSegments before splitting:',
      missingSegments,
    );
    missingSegments = splitSegments(missingSegments);
    beeXLogger.debug(
      '🚀 ~ StorageVideoComponent ~ missingSegments after splitting:',
      missingSegments,
    );

    if (!missingSegments?.length) {
      setGatewayVideos([]);
      return;
    }
    beeXLogger.debug("🚀 ~ StorageVideoComponent ~ missingSegments:", missingSegments)
    const newGatewayVideos = missingSegments.map((item) => ({
      from: secondsToTime(item.from),
      to: secondsToTime(item.to),
      hour: Math.floor(item.from / 3600),
      minute: Math.floor((item.from % 3600) / 60),
      duration: secondsToTime(item.to - item.from),
      recordId: 'on_gateway-' + item.from + '-' + item.to + '-' + serial,
      thumbnail: selectCamera?.thumbnail,
      serial: serial,
      encrypt: 'none',
      recordDate: dateRecord.format('YYYY-MM-DD'),
      state: item.type === 'gateway' ? 'on_gateway' : item.type === 'dvr' ? 'on_dvr' : item.state,
      tags: '',
      type: item.type === 'gateway' ? 'on_gateway' : item.type === 'dvr' ? 'on_dvr' : '',
      fileName: `${serial}_${item.from}_${item.to - item.from}`,
    }));
    beeXLogger.debug(
      '🚀 ~ StorageVideoComponent ~ newGatewayVideos:',
      newGatewayVideos,
    );

    setGatewayVideos(newGatewayVideos);
  }, [playbackLocalTimes, data, dateRecord, serial, selectCamera?.thumbnail]);

  const failedModalColumns = [
    {
      title: t('Serial'),
      dataIndex: 'serial',
      align: 'center',
    },
    {
      title: t('From'),
      dataIndex: 'from',
      align: 'center',
    },
    {
      title: t('To'),
      dataIndex: 'to',
      align: 'center',
    },
  ];

  return (
    <div className="storage-video-component">
      <div className="header-storage-component">
        <div className="picker-side" style={{ display: 'flex' }}>
          <DatePicker
            dateRender={(current) => {
              const style = {};
              if (
                camerasDate &&
                camerasDate.find((x) => x === current.format('MM-DD-YYYY'))
              ) {
                style.color = 'rgba(255, 255, 255, 0.85)';
                style.borderBottom = '1px solid #0e9347';
              }
              return (
                <div className="ant-picker-cell-inner" style={style}>
                  {current.date()}
                </div>
              );
            }}
            className="picker"
            format="DD-MM-YYYY"
            placeholder={t('Select date')}
            value={dateRecord}
            disabled={!serial}
            style={{
              width: '255px',
              marginLeft: '20px',
              height: '32px',
              marginRight: '20px',
            }}
            onChange={(value) => onChangeDate(value)}
          />
          <Select
            className="picker"
            value={timePicker}
            disabled={!timeRecord}
            onChange={(times) => onChangeTime(times)}
            style={{ width: '255px', height: '32px' }}
          >
            <Option value={1000}>{t('All time')}</Option>
            {timeRecord &&
              timeRecord?.map((time, index) => (
                <Option key={index} value={time.value}>
                  {time.title}
                </Option>
              ))}
          </Select>
        </div>
        <div className="add-camera">
          <Popover
            ref={popoverRef}
            placement="bottomRight"
            title={null}
            content={<SystemMessage handleClosePopup={handleClosePopup} />}
            overlayClassName="system_message_popup"
            trigger="click"
            visible={visible}
            onVisibleChange={(visible) => {
              dispatch(
                updateRecoveryCameraPage({ analyticsVisibile: visible }),
              );
              return setVisible(visible);
            }}
            maskClosable={false}
          >
            <Button
              icon={<LineChartOutlined />}
              style={{ marginRight: 20 }}
              title="Disconnect event"
            >
              {t('Analytics')}
            </Button>
          </Popover>
          {displayVideos?.length > 0 &&
            permission?.split('_')?.find((item) => item === 'download') && (
              <Button
                loading={isDownloading}
                onClick={() => handleDownloadVideo(true, displayVideos)}
                icon={<ArrowDownOutlined />}
                style={{ marginRight: 20 }}
              >
                {t('Download videos')}
              </Button>
            )}
          <Button
            onClick={() => {
              dispatch(updateRecoveryCameraPage({ searchModalVisible: true }));
              setOpenSearchVideosModal(true);
            }}
            icon={<SearchOutlined />}
            style={{ marginRight: 20 }}
          >
            {t('Search')}
          </Button>
          <Button
            className="intro-add-camera"
            icon={<PlusOutlined />}
            size="middle"
            onClick={() => {
              handleAddCamera();
              introJs().exit();
            }}
          >
            {t('Add camera')}
          </Button>
        </div>
      </div>

      <div className="center-storage-component">
        {!isLoading ? (
          displayVideos ? (
            displayVideos.length > 0 ? (
              <Row gutter={[8, 8]}>
                <Col xs={24} md={12} lg={8}>
                  <div className="child-video upload-video-camera">
                    <Tooltip title={t('Upload video')}>
                      <div
                        className="add-button"
                        style={{
                          cursor: isUploadVideo ? 'pointer' : 'not-allowed',
                        }}
                        onClick={() => {
                          if (isUploadVideo) {
                            showModalUpload();
                          }
                        }}
                      >
                        <PlusOutlined
                          style={{
                            fontSize: 60,
                            color: isUploadVideo
                              ? 'rgb(198 198 198)'
                              : '#4f4d4d',
                          }}
                        />
                      </div>
                    </Tooltip>
                  </div>
                </Col>
                {displayVideos?.map((video, index) => {
                  const isGatewayOrDvr =
                    video?.type === 'on_gateway' || video?.type === 'on_dvr';
                  const isFallback = errorVideo?.some(
                    (item) => item === video?.recordId,
                  );
                  const isTranscode = isGatewayOrDvr || isFallback;
                  const isCurrentTranscode = currentVideo?.recordId === video?.recordId;

                  return (
                    <Col key={video.recordId} xs={24} md={12} lg={8}>
                      <div className="main-child-video">
                        <div className="streaming-video">
                          {isTranscode ? (
                            isCurrentTranscode ? (
                              <PlaybackPlayer
                                key={`playback-${isGatewayOrDvr ? 'gateway' : 'fallback'}-${video.recordId || ''}-${video.from || ''}-${video.to || ''}`}
                                recordId={video.recordId}
                                queryTime={moment(`${moment(dateRecord).format('YYYY-MM-DD')} ${video.from}`).valueOf()}
                                serial={video?.serial}
                                thumbnail={
                                  video?.thumbnail || selectCamera?.thumbnail
                                }
                                allowFullScreen={true}
                                src={video.secureLink && video.secureLink}
                                logContent={JSON.stringify({
                                  recordId: video.recordId,
                                  isSaveAs: decryptData?.fileName ? true : false,
                                  cameraName:
                                    decryptData?.fileName ||
                                    video.fileName?.replace('.mp4', ''),
                                  actionDate: moment(video.recordDate).format(
                                    'DD-MM-YYYY',
                                  ),
                                })}
                                logSerial={video.serial}
                                handleReportActionLog={handleReportActionLog}
                                permission={permission && permission.split('_')}
                                variantPage="camera"
                                autoPlay={true}
                                fromTime={video?.from}
                                toTime={video?.to}
                                selectedDate={dateRecord}
                                type={isGatewayOrDvr ? (video?.type || 'on_gateway') : 'stream_fallback'}
                                onDebugLog={(stepKey, stepData) =>
                                  updateVideoDebugLog(video.recordId, stepKey, stepData)
                                }
                              />
                            ) : (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  background: '#000',
                                }}
                              >
                                <img
                                  src={
                                    video.thumbnail && video.thumbnail.match(/http/g)
                                      ? video.thumbnail
                                      : selectCamera?.thumbnail || Thumbnail
                                  }
                                  alt="thumbnail"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                  }}
                                />
                              </div>
                            )
                          ) : (
                            <PlayerComponent
                              key={`player-${video.recordId || ''}-${video.from || ''}-${video.to || ''}`}
                              recordId={video.recordId}
                              type="video"
                              codec={video.codec || selectCamera?.codec}
                              loading={Boolean(video.loading)}
                              permission={permission && permission.split('_')}
                              src={video.secureLink && video.secureLink}
                              fromTime={video?.from}
                              toTime={video?.to}
                              expectedDuration={video?.duration}
                              poster={
                                video.thumbnail && video.thumbnail.match(/http/g)
                                  ? video.thumbnail
                                  : selectCamera?.thumbnail || Thumbnail
                              }
                              logContent={JSON.stringify({
                                recordId: video.recordId,
                                isSaveAs: decryptData?.fileName ? true : false,
                                cameraName:
                                  decryptData?.fileName ||
                                  video.fileName?.replace('.mp4', ''),
                                actionDate: moment(video.recordDate).format(
                                  'DD-MM-YYYY',
                                ),
                              })}
                              logSerial={video.serial}
                              handleReportActionLog={handleReportActionLog}
                              dateTime={`${moment(video?.recordDate).format('DD-MM-YYYY') ??
                                ''
                                }_${video?.from?.replaceAll(':', '-') ?? ''}_${video?.to?.replaceAll(':', '-') ?? ''
                                }`}
                              onError={(err) => {
                                beeXLogger.error("🚀 ~ StorageVideoComponent ~ err:", err)
                                const errMsg =
                                  err?.message ||
                                  (typeof err === 'string'
                                    ? err
                                    : err?.code
                                      ? `Media Error Code ${err.code}`
                                      : 'HTML5 Media Playback Error');
                                updateVideoDebugLog(video.recordId, 's3Playback', {
                                  status: 'error',
                                  text: 'S3 video playback failed',
                                  error: errMsg,
                                });
                                updateVideoDebugLog(video.recordId, 'fallback', {
                                  status: 'triggered',
                                  text: 'S3 failed, switching to gateway/DVR stream fallback',
                                });
                                updateVideoDebugLog(video.recordId, 'gatewayStream', {
                                  status: 'pending',
                                  text: 'Requesting fallback stream from gateway/DVR...',
                                });
                                setVideos((state) =>
                                  state?.map((v) =>
                                    v?.recordId === video.recordId ||
                                    v?.dataRecordIds?.includes(video.recordId)
                                      ? { ...v, loading: false }
                                      : v,
                                  ),
                                );
                                setErrorVideo((prev) => {
                                  return [...new Set([...prev, video.recordId])];
                                });
                                setCurrentVideo(video);
                              }}
                              onPlay={() => {
                                updateVideoDebugLog(video.recordId, 's3Playback', {
                                  status: 'pending',
                                  text: 'Loading S3 video into player...',
                                });
                              }}
                              onPlaying={() => {
                                updateVideoDebugLog(video.recordId, 's3Playback', {
                                  status: 'success',
                                  text: 'S3 video playing normally',
                                });
                                updateVideoDebugLog(video.recordId, 'fallback', {
                                  status: 'skipped',
                                  text: 'S3 playback succeeded, fallback not needed',
                                });
                              }}
                              onTimeUpdate={(e) => {
                                const currentTime = e?.target?.currentTime || 0;
                                if (currentTime > 0) {
                                  updateVideoDebugLog(video.recordId, 's3Playback', {
                                    status: 'success',
                                    text: 'S3 video playing normally',
                                  });
                                  updateVideoDebugLog(video.recordId, 'fallback', {
                                    status: 'skipped',
                                    text: 'S3 playback succeeded, fallback not needed',
                                  });
                                }
                              }}
                            />
                          )}
                          {video.decrypting && (
                            <div className="decrypt no-select">
                              {t('Decrypting...')}
                            </div>
                          )}
                          {(!isTranscode ? (!video.loading && !video.secureLink) : !isCurrentTranscode) && (
                            <>
                              {permission &&
                                permission.split('_') &&
                                permission
                                  .split('_')
                                  .find(
                                    (item) =>
                                      item === 'playback' || item === 'download',
                                  ) && (
                                  <img
                                    onClick={() => {
                                      if (isTranscode) {
                                        setCurrentVideo(video);
                                      } else {
                                        onViewVideo(
                                          dataSelectSource?.find(
                                            (item) => item?.id === video.recordId,
                                          )?.selectRecordId ?? video.recordId,
                                          video.encrypt !== 'none',
                                        );
                                      }
                                    }}
                                    className="icon-play"
                                    src={
                                      video.encrypt === 'none'
                                        ? IconPlayButton
                                        : IconDecryptionButton
                                    }
                                    alt="play-button"
                                    style={{
                                      cursor: 'pointer',
                                    }}
                                  />
                                )}
                            </>
                          )}
                        </div>

                        <div className="description">
                          <div className="des">{`${video.from} - ${video.to}`}</div>
                          <div style={{ display: 'flex' }}>
                            <div style={{ marginRight: 10, marginLeft: 10 }}>
                              {video.tags && (
                                <Tag style={{ margin: 3 }}>
                                  <>
                                    {video.tags.split(';')[0].slice(0, 20)}{' '}
                                    {(video.tags.split(';').length > 1 ||
                                      video.tags.split(';')[0].length > 20) && (
                                        <span>...</span>
                                      )}
                                  </>
                                </Tag>
                              )}
                            </div>
                            {video?.type === 'on_gateway' && (
                              <Tag
                                color="#0e9347"
                                style={{ marginRight: 10, fontSize: 10 }}
                              >
                                {t('On Gateway')}
                              </Tag>
                            )}
                            {video?.type === 'on_dvr' && (
                              <Tag
                                color="#028fd5"
                                style={{ marginRight: 10, fontSize: 10 }}
                              >
                                {t('On DVR')}
                              </Tag>
                            )}
                            <Popover
                              trigger="click"
                              placement="topRight"
                              content={
                                <VideoInformation
                                  video={video}
                                  onUpdateVideoTagsSuccess={() =>
                                    getListStorage(serial, dateRecord)
                                  }
                                  dataSelectSource={dataSelectSource}
                                  setDataSelectSource={setDataSelectSource}
                                  debugLog={videoDebugLogsMap?.[video.recordId]}
                                />
                              }
                            // title={t('Video information')}
                            >
                              <Tooltip title={t('Video information')}>
                                <div className="icon-question">
                                  <img src={IconExclamation} alt="question" />
                                </div>
                              </Tooltip>
                            </Popover>
                            {video.state === 'manual_upload' &&
                              dataUser.role === 'admin' && (
                                <Popconfirm
                                  title="Are you sure to delete this record?"
                                  onConfirm={() => handleDeleteRecord(video)}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Tooltip title={t('Delete')}>
                                    <DeleteOutlined
                                      style={{
                                        marginLeft: '16px',
                                        fontSize: '20px',
                                        color: 'rgb(245, 47, 57)',
                                      }}
                                    />
                                  </Tooltip>
                                </Popconfirm>
                              )}
                          </div>
                        </div>
                      </div>
                    </Col>
                  )
                })}
              </Row>
            ) : (
              <Row gutter={[8, 8]}>
                <Col xs={24} md={12} lg={8}>
                  <div className="child-video upload-video-camera">
                    <Tooltip title={t('Upload video')}>
                      <div
                        className="add-button"
                        style={{
                          cursor: isUploadVideo ? 'pointer' : 'not-allowed',
                        }}
                        onClick={() => {
                          if (isUploadVideo) {
                            showModalUpload();
                          }
                        }}
                      >
                        <PlusOutlined
                          style={{
                            fontSize: 60,
                            color: isUploadVideo
                              ? 'rgb(198 198 198)'
                              : '#4f4d4d',
                          }}
                        />
                      </div>
                    </Tooltip>
                  </div>
                </Col>
              </Row>
            )
          ) : (
            <div className="empty-video">
              <Empty
                image={IconCameraEmpty}
                imageStyle={{
                  height: 100,
                }}
                description={<span>{t('No cameras are selected!')}</span>}
              />
            </div>
          )
        ) : (
          <Spinner />
        )}
      </div>

      <Modal
        title={
          <span style={{ color: '#ff4d4f' }}>
            <ExclamationCircleOutlined style={{ marginRight: 8 }} />
            {t('Download failed')}
          </span>
        }
        open={isFailedModalVisible}
        onCancel={() => setFailedModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setFailedModalVisible(false)}
          >
            {t('Close')}
          </Button>,
        ]}
        width={600}
      >
        <Table
          size="small"
          rowKey="key"
          dataSource={failedDownloads}
          columns={failedModalColumns}
          pagination={false}
          bordered
        />
      </Modal>

      <StepsIntro
        enabled={stepsEnabled}
        steps={dataSteps}
        initialStep={initialStep}
        onExit={() => setStepsEnabled(false)}
      />

      {isOpenSearchVideosModal && (
        <SearchVideosModal
          onClose={() => {
            setOpenSearchVideosModal(false);
            dispatch(
              updateRecoveryCameraPage({
                searchModalVisible: false,
                listStorageFileByTag: [],
                searchVideoTag: '',
              }),
            );
            dispatch(setListStorageFileByTag(null));
            dispatch(generateLinkStorageFileReturn(null));
          }}
        />
      )}
    </div>
  );
};

export default StorageVideoComponent;
